import { ChangeDetectionStrategy, Component } from '@angular/core';

import { SharedLanguageService } from '@language';
import {
    BaseNestedFieldDefinition,
    ComplexMapFieldValue,
    DialogConfig,
    FieldDefinitionHelper,
    FieldValue
} from '@stylehaven/components';
import { isSimpleType } from '../../common/entities/common/simple-type.entity';

@Component({
    standalone: false,
    selector: 'med-complex-value-dialog',
    templateUrl: './complex-value-dialog.component.html',
    styleUrls: ['./complex-value-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ComplexValueDialogComponent {
    readonly complexValue: ComplexMapFieldValue;
    readonly complexFieldDefinition: BaseNestedFieldDefinition;

    constructor(
        dialogConfig: DialogConfig,
        private readonly translateService: SharedLanguageService
    ) {
        if (dialogConfig.data) {
            this.complexValue = dialogConfig.data.complexValue;
            this.complexFieldDefinition = dialogConfig.data.complexFieldDefinition;
        }
    }

    isSimpleType(fieldDef: BaseNestedFieldDefinition): boolean {
        return FieldDefinitionHelper.IsElementary(fieldDef);
    }

    isListType(fieldDef: BaseNestedFieldDefinition): boolean {
        return FieldDefinitionHelper.IsElementaryList(fieldDef);
    }

    isMapType(fieldDef: BaseNestedFieldDefinition): boolean {
        return FieldDefinitionHelper.IsMap(fieldDef);
    }

    isComplexListType(fieldDef: BaseNestedFieldDefinition): boolean {
        return FieldDefinitionHelper.IsComplexList(fieldDef);
    }

    getSimpleValue(fieldDef: BaseNestedFieldDefinition, value: FieldValue): string {
        const key: string = fieldDef.Key;

        if (isSimpleType(value)) {
            return this.cleanValue(value);
        }

        return this.cleanValue(value[key]);
    }

    getListValues(fieldDef: BaseNestedFieldDefinition, value: ComplexMapFieldValue): string[] {
        const childDef: BaseNestedFieldDefinition = (fieldDef.Children ?? [])[0];
        const listValue: FieldValue = value[fieldDef.Key] ?? null;

        if (isSimpleType(listValue)) {
            return [this.cleanValue(listValue)];
        }

        const childList: FieldValue = (listValue[0] as ComplexMapFieldValue)[childDef.Key] as FieldValue;
        let displayListValue: string[];

        if (Array.isArray(childList)) {
            displayListValue = childList.map(childListValue => this.cleanValue(childListValue));
        } else {
            displayListValue = [this.cleanValue(childList)];
        }

        return displayListValue;
    }

    getMapValue(fieldDef: BaseNestedFieldDefinition, value: FieldValue): string {
        if (isSimpleType(value)) {
            return this.cleanValue(value);
        }

        const childLabels: Record<string, string> = {};
        fieldDef.Children?.forEach(child => (childLabels[child.Key] = child.Label));

        const displayValue: string = Object.keys(value)
            .map(key => `${childLabels[key]}: ${this.cleanValue(value[key])}`)
            .join('<br />');

        return !!displayValue && displayValue !== '' ? displayValue : '-';
    }

    getComplexListValues(fieldDef: BaseNestedFieldDefinition, value: ComplexMapFieldValue): string[] {
        const childDef: BaseNestedFieldDefinition = (fieldDef.Children ?? [])[0];
        const listValue: FieldValue = value[fieldDef.Key] ?? null;

        if (isSimpleType(listValue)) {
            return [this.cleanValue(listValue)];
        }

        const childList: FieldValue = (listValue[0] as ComplexMapFieldValue)[childDef.Key] as FieldValue;

        let displayListValue: string[];

        if (Array.isArray(childList)) {
            displayListValue = childList.map(childListValue => this.getMapValue(childDef, childListValue));
        } else {
            displayListValue = childList ? [this.cleanValue(childList)] : [];
        }

        return displayListValue;
    }

    private cleanValue(value: any): string {
        if (Array.isArray(value) && value.length === 1) {
            return this.cleanValue(value[0]);
        }

        if (!isSimpleType(value)) {
            return value?.toString() ?? '-';
        }

        const stringValue: string = value?.toString() ?? '-';
        const clean: string = stringValue.trim();
        const optionalBooleanTranslate: string = this.optionalTranslateBoolean(clean);

        return optionalBooleanTranslate;
    }

    private optionalTranslateBoolean(stringValue: string): string {
        if (stringValue.toLowerCase() === 'true' || stringValue.toLowerCase() === 'false') {
            return this.translateService.instant(`overrides.BooleanField.${stringValue.toLowerCase()}`);
        }

        return stringValue;
    }
}
